"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Turnstile, TURNSTILE_ENABLED } from "./turnstile"
import {
  CAPTCHA_FIELD,
  FORM_TOKEN_FIELD,
  HONEYPOT_FIELD,
} from "@/lib/spam-fields"

/**
 * Wires the client-side half of the anti-spam layers into any form:
 *   - a CSS-hidden honeypot input
 *   - a signed render-time token fetched from /api/form-token (timing check)
 *   - a Cloudflare Turnstile widget
 *
 * Usage:
 *   const spam = useSpamGuard()
 *   ...
 *   <form>{spam.fields} ... </form>
 *   // on submit: axios.post(url, { ...data, ...spam.getSpamPayload() })
 *   // after success: spam.reset()
 *
 * None of these add friction beyond the captcha widget; the server re-checks
 * all of them, so a client that skips them is still rejected downstream.
 */
export function useSpamGuard() {
  const honeypotRef = useRef<HTMLInputElement>(null)
  const [formToken, setFormToken] = useState("")
  const [captchaToken, setCaptchaToken] = useState("")
  const [captchaFailed, setCaptchaFailed] = useState(false)

  useEffect(() => {
    let active = true
    fetch("/api/form-token", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (active && d?.token) setFormToken(d.token)
      })
      .catch(() => {
        /* token fetch failed — submit will be dropped server-side */
      })
    return () => {
      active = false
    }
  }, [])

  const getSpamPayload = useCallback(
    (): Record<string, string> => ({
      [HONEYPOT_FIELD]: honeypotRef.current?.value ?? "",
      [FORM_TOKEN_FIELD]: formToken,
      [CAPTCHA_FIELD]: captchaToken,
    }),
    [formToken, captchaToken]
  )

  const reset = useCallback(() => {
    if (honeypotRef.current) honeypotRef.current.value = ""
    setCaptchaToken("")
  }, [])

  // When Turnstile is configured, a submission without a captcha token is
  // rejected 403 by the server, so gate the form on it. When it isn't
  // configured (e.g. local dev) there's no token to wait for and the server
  // skips the captcha layer — so the form is always "ready".
  const captchaRequired = TURNSTILE_ENABLED
  const ready = !captchaRequired || Boolean(captchaToken)

  const fields = (
    <>
      {/* Honeypot: off-screen, ignored by assistive tech, not tabbable. A real
          user never sees or fills it; bots that auto-fill inputs get dropped. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "auto",
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        <label htmlFor={HONEYPOT_FIELD}>Company URL</label>
        <input
          ref={honeypotRef}
          id={HONEYPOT_FIELD}
          name={HONEYPOT_FIELD}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>
      <Turnstile
        onToken={(t) => {
          setCaptchaFailed(false)
          setCaptchaToken(t)
        }}
        onExpire={() => setCaptchaToken("")}
        onError={() => setCaptchaFailed(true)}
        className="my-3"
      />
      {captchaFailed && (
        <p className="text-xs text-red-500 -mt-1 mb-2">
          No se pudo cargar la verificación de seguridad. Recarga la página e
          inténtalo de nuevo.
        </p>
      )}
    </>
  )

  return {
    fields,
    getSpamPayload,
    reset,
    formToken,
    captchaToken,
    /** True once the form may be submitted (captcha solved, or not required). */
    ready,
    /** True when a Turnstile widget is active and a token is expected. */
    captchaRequired,
  }
}
